import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readFiniteNumber(record: Record<string, unknown>, key: string): number {
  const value = record[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error('invalid_talent_discovery_meter');
  }
  return value;
}

function parseTalentDiscoveryMeter(value: unknown): TalentDiscoveryMeter {
  if (!isRecord(value)) throw new Error('invalid_talent_discovery_meter');
  if (typeof value.can_send !== 'boolean') throw new Error('invalid_talent_discovery_meter');
  const periodStart = typeof value.period_start === 'string' ? value.period_start : null;
  const periodEnd = typeof value.period_end === 'string' ? value.period_end : null;

  return {
    monthly_included: readFiniteNumber(value, 'monthly_included'),
    monthly_consumed: readFiniteNumber(value, 'monthly_consumed'),
    monthly_remaining: readFiniteNumber(value, 'monthly_remaining'),
    period_start: periodStart,
    period_end: periodEnd,
    credits_purchased: readFiniteNumber(value, 'credits_purchased'),
    credits_consumed: readFiniteNumber(value, 'credits_consumed'),
    credits_remaining: readFiniteNumber(value, 'credits_remaining'),
    total_remaining: readFiniteNumber(value, 'total_remaining'),
    can_send: value.can_send,
  };
}

export interface TalentDiscoveryMeter {
  monthly_included: number;
  monthly_consumed: number;
  monthly_remaining: number;
  period_start: string | null;
  period_end: string | null;
  credits_purchased: number;
  credits_consumed: number;
  credits_remaining: number;
  total_remaining: number;
  can_send: boolean;
}

export function useTalentDiscoveryMeter(enabled: boolean = true) {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  const query = useQuery({
    queryKey: ['talent-discovery-meter', orgId],
    queryFn: async (): Promise<TalentDiscoveryMeter> => {
      const { data, error } = await supabase.rpc('get_talent_discovery_meter', {
        p_org_id: orgId!,
      });
      if (error) throw error;
      return parseTalentDiscoveryMeter(data);
    },
    enabled: !!orgId && enabled,
    staleTime: 30 * 1000,
  });

  return {
    meter: query.data,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

export interface InviteScoreState {
  invite_id: string;
  accepted_application_id: string | null;
  candidate_id: string | null;
  hire_org_id: string | null;
  target_hire_job_id: string | null;
  score_state: 'processing' | 'ready' | 'unavailable' | null;
  scored_at: string | null;
  total_score: number | null;
}

function normalizeScoreState(value: string | null): InviteScoreState['score_state'] {
  return value === 'processing' || value === 'ready' || value === 'unavailable' ? value : null;
}

export function useInviteScoreStates(enabled: boolean = true) {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  const query = useQuery({
    queryKey: ['talent-discovery-invite-score-states', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('talent_discovery_invite_score_state')
        .select('*')
        .eq('hire_org_id', orgId!);
      if (error) throw error;

      const map: Record<string, InviteScoreState> = {};
      for (const row of data ?? []) {
        if (!row.invite_id) continue;
        map[row.invite_id] = {
          ...row,
          invite_id: row.invite_id,
          score_state: normalizeScoreState(row.score_state),
        };
      }
      return map;
    },
    enabled: !!orgId && enabled,
    staleTime: 30 * 1000,
  });

  return {
    scoreStates: query.data ?? {},
    isLoading: query.isLoading,
  };
}
